import { ConvexError } from "convex/values";

// ============================================================================
// Erreurs destinées à l'utilisateur
// ============================================================================
//
// Chaque erreur a un code (la clé) et un gabarit anglais. Les trous `{{nom}}`
// sont remplis par les `params` passés à `appError`.
//
// Le client reçoit `{ code, message, params }` (cf. `AppErrorData`) :
// - l'UI traduit `code` dans la langue de l'utilisateur, en réinjectant
//   `params` (src/locales/<lang>/errors.json) ;
// - Nolë et les clients MCP, qui ne passent pas par l'UI, lisent `message`,
//   la phrase anglaise déjà assemblée.
//
// Ajouter un code ici impose d'ajouter sa traduction dans
// src/locales/fr/errors.json (l'anglais est ce gabarit même ; le typage et
// src/i18n/locales.test.ts vérifient les clés et les variables).
const errors = {
  CANVAS_NOT_FOUND: "This canvas does not exist.",
  NODE_NOT_FOUND: "This node does not exist in this canvas.",
  NODE_NOT_IN_TRASH: "Only a node in the trash can be deleted for good.",
  NODE_DATA_NOT_FOUND_FOR_NODE: "No nodeData is linked to this node.",
  NODE_DATA_NOT_FOUND: "This nodeData does not exist.",
  RICH_TEXT_FIELD_NOT_SYNCED: "This field is no longer a formatted document.",
  RICH_TEXT_FIELD_DOC_RESET: "This document was reset. Close and reopen it to keep editing.",
  UNAUTHORIZED_USER: "You are not authenticated or do not have access.",
  USER_NOT_FOUND: "This user does not exist.",
  ACCOUNT_DELETION_EMAIL_MISMATCH:
    "This is not the email address of your account.",
  ACCOUNT_DELETION_NO_EMAIL:
    "This account has no email address, so it cannot be deleted from here. Please contact support.",
  EMAIL_NOT_FOUND: "No user found with this email address.",
  SHARING_WITH_SELF: "You cannot share with yourself.",
  EDGE_SELF_CONNECTION_NOT_ALLOWED: "A node cannot be connected to itself.",
  EDGE_NOT_FOUND: "This connection does not exist in this canvas.",
  EDGES_MUST_SHARE_CANVAS: "These connections must belong to the same canvas.",
  EDGE_SOURCE_NOT_FOUND: "The source node of this connection does not exist.",
  EDGE_TARGET_NOT_FOUND: "The target node of this connection does not exist.",
  EDGE_ALREADY_EXISTS: "These two nodes are already connected.",
  EDGE_ID_ALREADY_TAKEN:
    "This connection id is already used in another canvas.",
  SOURCE_AND_TARGET_CANVAS_MUST_BE_DIFFERENT:
    "Source and target canvases must be different.",
  NODES_MUST_SHARE_CANVAS: "These nodes must belong to the same canvas.",
  NODE_ID_ALREADY_TAKEN: "This node id is already used in another canvas.",
  NODE_PARENT_MUST_BE_A_FRAME:
    "A node can only belong to a frame of the same canvas.",
  FRAME_CANNOT_BE_NESTED: "A frame cannot be placed inside another frame.",
  NODE_ALREADY_IN_A_FRAME: "This node already belongs to a frame.",
  FRAME_NEEDS_TWO_NODES:
    "A frame groups at least two nodes: one node on its own is a border, not a group.",
  INSUFFICIENT_PERMISSIONS:
    "You do not have the required permission for this action.",
  THREAD_NOT_FOUND_OR_FORBIDDEN: "Thread not found or access denied.",
  TEMPLATE_NOT_FOUND: "This node template does not exist.",
  TOKEN_NOT_FOUND: "This API token does not exist.",
  TOKEN_NAME_REQUIRED: "Token name is required.",
  TOKEN_REVOKED: "This token has been revoked and can no longer be edited.",
  TOKEN_CANVAS_NOT_ALLOWED:
    "This API token only opens some canvases: call list_canvases to see them.",
  SANDBOX_HOST_INVALID:
    "This is not a website address: write one such as api.github.com, without an IP address or a *.",
  SANDBOX_HOSTS_FULL:
    "This canvas already allows as many websites as it can: remove one first.",
  SANDBOX_BUSY:
    "Nolë is using its computer right now: you can turn it off once it is done.",
  INVALID_USAGE_PERIOD:
    "Invalid usage period. Expected two YYYY-MM-DD dates, in order, at most 366 days apart.",
  UNSUPPORTED_FILE_TYPE: "This file type is not allowed.",
  INVALID_FILE_SIZE: "This file size could not be read.",
  FILE_TOO_LARGE: "This file is too large.",
  TOO_MANY_FILES: "Too many files in a single upload.",
  AUDIO_TOO_LARGE: "This recording is too long to be transcribed.",
  IMAGE_GENERATION_WRONG_NODE_TYPE:
    "Images can only be generated on an image node.",
  IMAGE_GENERATION_EMPTY_PROMPT: "A prompt is required to generate images.",
  NOLE_EMPTY_MESSAGE: "Write a message or add a comment before sending.",
  IMAGE_GENERATION_ALREADY_RUNNING:
    "A generation is already running on this node.",
  IMAGE_GENERATION_MODEL_NO_REFERENCES:
    "This model does not accept reference images. Pick another model, or remove the references.",
  IMAGE_GENERATION_TOO_MANY_REFERENCES:
    "Too many reference images for this model.",
  IMAGE_GENERATION_MODEL_REQUIRES_REFERENCES:
    "This model edits images: connect at least one image node as input, or pick another model.",
  GENERATION_MENTION_NOT_ATTACHED:
    "The prompt mentions an image that is not sent with this generation (unplugged or excluded). Reconnect it or remove the mention.",
  VIDEO_GENERATION_WRONG_NODE_TYPE:
    "Videos can only be generated on a video node.",
  VIDEO_GENERATION_EMPTY_PROMPT: "A prompt is required to generate a video.",
  VIDEO_GENERATION_ALREADY_RUNNING:
    "A video generation is already running on this node.",
  VIDEO_GENERATION_INVALID_SETTINGS:
    "This model does not support these settings (duration, resolution or aspect ratio).",
  VIDEO_GENERATION_FRAME_NOT_SUPPORTED:
    "This model does not take this frame (start or end). Change the image's role or pick another model.",
  VIDEO_GENERATION_DUPLICATE_FRAME:
    "Only one image can be the start frame, and one the end frame.",
  VIDEO_GENERATION_MIXED_FRAMES_AND_REFERENCES:
    "Start/end frames and reference images cannot be combined: OpenRouter would ignore the references. Use one or the other.",
  VIDEO_GENERATION_MODEL_NO_REFERENCES:
    "This model does not accept reference images. Use the images as frames, pick another model, or ignore them.",
  VIDEO_GENERATION_TOO_MANY_REFERENCES:
    "Too many reference images for this model.",
  TRANSCRIPTION_WRONG_NODE_TYPE:
    "Only audio and video nodes can be transcribed.",
  TRANSCRIPTION_NO_FILE: "This node has no file to transcribe.",
  TRANSCRIPTION_FILE_TOO_LARGE: "This file is too large to be transcribed.",
  TRANSCRIPTION_UNAVAILABLE:
    "Transcription is not available for this file on this deployment.",
  TRANSCRIPTION_ALREADY_RUNNING:
    "A transcription is already running on this node.",
  BOOKMARK_NOT_FOUND: "This bookmark does not exist.",
  BOOKMARK_EMPTY_SELECTION: "A bookmark needs at least one node to point at.",
  BOOKMARK_LABEL_TOO_LONG:
    "This bookmark name is too long ({{max}} characters max).",
  BOOKMARKS_MUST_SHARE_CANVAS:
    "These bookmarks must belong to the same canvas.",
  CANVAS_ICON_TOO_LONG: "This canvas icon is too long. Use a single emoji.",
  CANVAS_COVER_IMAGE_INVALID: "This cover image cannot be used.",
  RATE_LIMITED: "Too many requests. Try again in {{seconds}} s.",
  ID_GENERATION_FAILED: "Could not generate a unique id. Please try again.",
  DISPLAY_NAME_TOO_LONG: "This name is too long ({{max}} characters max).",
  MEMORY_TOO_LONG:
    "Your memories are too long ({{current}}/{{max}} characters). Remove or shorten some before saving.",
  SKILL_NOT_FOUND: "This skill does not exist.",
  SKILL_FORBIDDEN: "You do not have access to this skill.",
  SKILL_FRONTMATTER_MISSING_FIELD: "Missing '{{field}}' in skill frontmatter.",
  SKILL_NAME_TAKEN: "A skill named '{{name}}' already exists.",
  SKILL_ATTACHMENT_NAME_TAKEN:
    "An attachment named '{{name}}' already exists on this skill.",
  VERSION_NOT_FOUND: "This version does not exist.",
  VERSION_NAME_EMPTY: "A version name cannot be empty.",
  VERSION_NODE_TYPE_CHANGED:
    "This version was saved when this block had another type, so it can no longer be restored.",
  VERSION_ALREADY_NAMED:
    "This content is already saved as “{{name}}”. Rename that version instead.",
  INVALID_DOCUMENT: "This document is invalid.",
  INVALID_NODE_VALUES: "Invalid value(s) for this custom node.",
  CUSTOM_NODE_NEEDS_TEMPLATE: "A custom node needs a template.",
  NODE_NOT_CONVERTIBLE: "This node can't be converted to a custom node.",
  TEMPLATE_DEFINITION_INVALID: "Invalid template definition.",
  TEMPLATE_NAME_REQUIRED: "A template needs a name.",
  TEMPLATE_SIZE_INVALID: "Invalid template size: {{size}}.",
  TEMPLATE_DESCRIPTION_TOO_LONG: "Template description is too long.",
  TEMPLATE_IN_USE:
    "This template is still in use. Delete what uses it first, trash included.",
  TITLE_COLUMN_NOT_DELETABLE:
    '"{{name}}" is the title column: it can be renamed, but not deleted.',
  TITLE_COLUMN_STAYS_TEXT:
    '"{{name}}" is the title column: it stays a text column.',
  NOT_A_DATABASE: "This node is not a database.",
  NOT_A_TABLE: "This node is not a table.",
  DATABASE_NOT_FOUND: "This database does not exist.",
  DATABASE_TOO_MANY_COLUMNS: "A database has at most {{max}} columns.",
  TABLE_TOO_MANY_COLUMNS_FOR_DATABASE:
    "A database holds at most {{max}} columns; this table would have {{columns}}. Delete some columns first.",
  DATABASE_TOO_MANY_ROWS_FOR_TABLE:
    "A table holds at most {{max}} rows; this database has {{rows}}.",
  DATABASE_TOO_LARGE_FOR_TABLE:
    "This database is too large to fit in a table (512 KB at most).",
  DATABASE_TOO_MANY_ROWS_TO_REPLACE:
    "This database has more than {{max}} rows: import in append mode instead.",
  TOO_MANY_ROWS_PER_WRITE:
    "Too many rows in one write ({{rows}}): send at most {{max}} at a time.",
  TOO_MANY_ROWS_AT_ONCE: "Too many rows: at most {{max}} at a time.",
  TABLE_BEING_MIGRATED:
    "This table is being converted to the new table format. Try again in a few minutes.",
  TABLE_ROWS_BEING_CLEANED_UP:
    "This table's previous rows are still being cleaned up. Try again in a moment.",
  ROW_NOT_FOUND: "This row does not exist in this database.",
  ROW_CREATION_FAILED: "The row could not be created.",
  NODE_NOT_OF_DATABASE_TEMPLATE:
    "Only a node of this database's template can join it.",
  NODE_ALREADY_A_ROW: "This node is already a row of a database.",
  VIEW_NOT_FOUND: "This view does not exist.",
  DATABASE_TABLE_SHOWS_ALL_ROWS: "The database's table shows all its rows.",
  DATABASE_VIEW_NEEDS_DATABASE: "A database view needs its database.",
  DATABASE_VIEW_WRONG_CANVAS:
    "A database view goes on the canvas of its database.",
  DATABASE_VIEW_MOVES_WITH_DATABASE:
    "A database view moves with its database: add the database to what you move.",
  VIEW_GROUP_BY_FIELD_INVALID: "This field can't group a kanban.",
  VIEW_DATE_FIELD_INVALID: "A timeline's date must be a date field.",
  VIEW_END_FIELD_INVALID: "A timeline's end date must be a date field.",
  VIEW_COVER_FIELD_INVALID: "A gallery's cover must be a file field.",
  RELATION_TARGET_NOT_SET:
    'Relation "{{field}}": choose the database it links to first.',
  RELATION_ROW_NOT_FOUND:
    'Relation "{{field}}": a linked row was not found in its database.',
  RELATION_TOO_MANY_LINKS:
    'Relation "{{field}}": a row links at most {{max}} rows.',
  RELATION_DATABASE_NOT_FOUND:
    'Relation "{{field}}": database not found on this canvas.',
} as const;

export default errors;

export type ErrorCode = keyof typeof errors;

export type ErrorParams = Record<string, string | number>;

/** Ce que porte `ConvexError.data` pour toute erreur levée par `appError`. */
export type AppErrorData = {
  code: ErrorCode;
  /** Phrase anglaise complète : gabarit rempli, puis `detail` éventuel. */
  message: string;
  params?: ErrorParams;
};

function fill(template: string, params?: ErrorParams): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    params?.[key] !== undefined ? String(params[key]) : "",
  );
}

/**
 * L'erreur à lever pour tout refus que l'utilisateur peut voir.
 *
 * `detail` complète le message anglais seulement : précision technique (ids
 * inconnus, type du node…) utile à Nolë ou à un client MCP pour corriger son
 * appel, mais que l'UI traduite n'affiche pas.
 */
export function appError(
  code: ErrorCode,
  params?: ErrorParams,
  detail?: string,
): ConvexError<AppErrorData> {
  const base = fill(errors[code], params);
  return new ConvexError({
    code,
    message: detail ? `${base} ${detail}` : base,
    ...(params ? { params } : {}),
  });
}

export function isAppErrorData(data: unknown): data is AppErrorData {
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as { code?: unknown }).code === "string" &&
    typeof (data as { message?: unknown }).message === "string"
  );
}

/**
 * Le texte lisible d'une erreur, côté serveur (tools de Nolë, MCP) : la phrase
 * anglaise d'une `appError`, la chaîne d'une `ConvexError` brute, sinon le
 * `message` de l'erreur.
 */
export function errorText(error: unknown): string {
  if (error instanceof ConvexError) {
    if (typeof error.data === "string") return error.data;
    if (isAppErrorData(error.data)) return error.data.message;
    return JSON.stringify(error.data);
  }
  return error instanceof Error ? error.message : String(error);
}
